import {
  Box,
  Image,
  Text,
  VStack,
  HStack,
  IconButton,
  useColorModeValue,
  Tooltip,
  Switch,
  FormControl,
  FormLabel,
  Spinner,
} from '@chakra-ui/react';
import { useTheme } from '@partiu/ui/contexts';
import { FaEdit, FaTrash } from 'react-icons/fa';

export interface BenefitCardProps {
  title: string;
  image: string;
  color?: string;
  selected?: boolean;
  disabled?: boolean;
  partiuPlan?: boolean;
  inverted?: boolean;
  small?: boolean;
  editable?: boolean;
  canEdit?: boolean; // Mantido para compatibilidade
  removeCheckbox?: boolean;
  isLoading?: boolean;
  onClick: (event?: React.MouseEvent) => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onToggle?: () => void;
}

export const BenefitCard = ({
  title,
  image,
  color = '#000',
  selected = false,
  disabled = false,
  inverted = false,
  small = false,
  editable = false,
  removeCheckbox = false,
  isLoading = false,
  partiuPlan = false,
  onClick,
  onEdit,
  onDelete,
  onToggle,
}: BenefitCardProps) => {
  const { colors } = useTheme();
  const isDisabled = inverted ? !disabled : disabled;
  const isActive = !isDisabled;

  // Theme-aware colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const activeBorderColor = color;
  const disabledBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const disabledTextColor = useColorModeValue('gray.400', 'gray.500');

  const cardSize = {
    width: small ? '100px' : '120px',
    height: small ? '100px' : '120px',
    padding: small ? '6px' : '8px',
  };

  const handleCardClick = (event: React.MouseEvent) => {
    // Previne propagação para evitar fechar/abrir accordions
    event.stopPropagation();
    // onClick sempre funciona, independente do disabled
    onClick(event);
  };

  const handleSwitchChange = () => {
    if (onToggle) {
      onToggle();
    } else {
      onClick();
    }
  };

  const handleActionClick = (event: React.MouseEvent, action: () => void) => {
    event.stopPropagation();
    action();
  };

  return (
    <VStack spacing={1} alignItems="center" position="relative">
      {/* Main Card */}
      <Box
        position="relative"
        backgroundColor={isActive ? bgColor : disabledBg}
        border="1px solid"
        borderColor={isActive ? activeBorderColor : borderColor}
        borderRadius="12px"
        {...cardSize}
        cursor="pointer"
        opacity={isDisabled ? 0.6 : 1}
        transition="all 0.3s ease"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={1}
        onClick={handleCardClick}
        role="button"
        tabIndex={0}
        aria-label={`${title} - ${isActive ? 'Ativado' : 'Desativado'}`}
        _hover={{
          transform: 'translateY(-2px)',
          boxShadow: `0 4px 15px -5px ${color}40`,
          borderColor: color,
          '& .action-buttons': {
            opacity: 1,
          },
          '& .checkbox-container': {
            opacity: 1,
          }
        }}
        _focus={{
          outline: 'none',
          ring: 1,
          ringColor: color,
          ringOffset: 1,
        }}
        _active={{
          transform: 'translateY(-1px)',
        }}
        opacity={isLoading ? 0.7 : undefined}
        pointerEvents={isLoading ? 'none' : undefined}
      >
        {isLoading && (
          <Box
            position="absolute"
            inset={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="inherit"
            bg="whiteAlpha.600"
            zIndex={20}
          >
            <Spinner size="sm" color="blue.500" />
          </Box>
        )}

        {/* Benefit Image */}
        <Box
          width={small ? '35px' : '40px'}
          height={small ? '35px' : '40px'}
          display="flex"
          alignItems="center"
          position="relative"
          justifyContent="center"
          borderRadius="8px"
          bg={useColorModeValue('gray.50', 'gray.700')}
          padding="4px"
        >
          {
            partiuPlan && (
              <Box
                position={'absolute'}
                mixBlendMode={'color'}
                width="100%"
                height={'100%'}
                borderRadius={{
                  base: '12px',
                  sm: '12px',
                  md: '12px',
                  lg: '24px',
                }}
                bg={colors.primaryColor}
              />
            )
          }
          <Image
            src={image}
            objectFit="contain"
            width="100%"
            height="100%"
            alt={title}
            fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 40 40'%3E%3Crect width='40' height='40' fill='%23e2e8f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23a0aec0' font-size='12'%3E?%3C/text%3E%3C/svg%3E"
          />
        </Box>

        {/* Benefit Title */}
        <Text
          fontSize={small ? '10px' : '11px'}
          fontWeight="600"
          color={isActive ? textColor : disabledTextColor}
          textAlign="center"
          lineHeight="1.1"
          noOfLines={2}
          px={1}
        >
          {title}
        </Text>

        {/* Action Buttons Overlay */}
        {(onDelete || (editable && selected && onEdit)) && (
          <HStack
            position="absolute"
            top="4px"
            right="4px"
            spacing={0.5}
            opacity={0}
            transition="opacity 0.2s"
            className="action-buttons"
          >
            {editable && selected && onEdit && (
              <Tooltip label="Editar benefício" hasArrow>
                <IconButton
                  aria-label="Editar"
                  icon={<FaEdit />}
                  size="xs"
                  colorScheme="blue"
                  variant="solid"
                  borderRadius="full"
                  onClick={(e) => handleActionClick(e, onEdit)}
                  zIndex={10}
                  minWidth="20px"
                  height="20px"
                />
              </Tooltip>
            )}

            {onDelete && (
              <Tooltip label="Remover benefício" hasArrow>
                <IconButton
                  aria-label="Remover"
                  icon={<FaTrash />}
                  size="xs"
                  colorScheme="red"
                  variant="solid"
                  borderRadius="full"
                  onClick={(e) => handleActionClick(e, onDelete)}
                  zIndex={10}
                  minWidth="20px"
                  height="20px"
                />
              </Tooltip>
            )}
          </HStack>
        )}

        {/* Checkbox no canto inferior direito - Only when not removeCheckbox */}
        {!removeCheckbox && (
          <Box
            position="absolute"
            bottom="4px"
            right="4px"
            className="checkbox-container"
            opacity={0.7}
            transition="opacity 0.2s"
            zIndex={5}
            onClick={(e) => {
              e.stopPropagation();
              handleSwitchChange();
            }}
            cursor="pointer"
            _hover={{
              opacity: 1,
            }}
          >
            <Box
              width="16px"
              height="16px"
              borderRadius="50%"
              border="2px solid"
              borderColor={isActive ? 'green.400' : 'gray.400'}
              backgroundColor={isActive ? 'green.400' : 'transparent'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              transition="all 0.2s ease"
              _hover={{
                transform: 'scale(1.1)',
                borderColor: isActive ? 'green.500' : 'gray.500',
                backgroundColor: isActive ? 'green.500' : 'gray.100',
              }}
            >
              {isActive && (
                <Box
                  width="6px"
                  height="6px"
                  borderRadius="50%"
                  backgroundColor="white"
                  transition="all 0.2s ease"
                />
              )}
            </Box>
          </Box>
        )}
      </Box>
    </VStack>
  );
};
